import { Injectable, inject } from '@angular/core';
import { Transition, UIRouter } from '@uirouter/angular';
import { MaintenanceModeStateNames } from '../../maintenance-mode/maintenance-mode-state';

@Injectable({ providedIn: 'root' })
export class MaintenanceModeService {
  private readonly uiRouter = inject(UIRouter);
  private active = false;
  private pendingTransition: Transition | null = null;

  get isActive(): boolean {
    return this.active;
  }

  get requestContext(): string | null {
    const transition = this.pendingTransition ?? this.uiRouter.globals.transition;
    const stateName = transition?.to().name ?? this.uiRouter.globals.current.name;
    if (!this.isJourneyState(stateName)) return null;
    const params = transition?.params('to') ?? this.uiRouter.globals.params;
    return JSON.stringify([params.shortCode, params.loanCode, params.dealKey]);
  }

  get isJourneyRequest(): boolean {
    return this.requestContext !== null;
  }

  constructor() {
    // Lazy loading can pause a transition before globals.transition is assigned.
    this.uiRouter.transitionService.onBefore({}, transition => {
      this.pendingTransition = transition;
    }, { priority: 10000 });
    this.uiRouter.transitionService.onError({}, transition => {
      if (this.pendingTransition === transition) this.pendingTransition = null;
    });
    // Existing request catch handlers may attempt a generic error redirect after a 503.
    this.uiRouter.transitionService.onBefore({ to: 'client.journey.**' }, transition => {
      if (!this.active) return;
      return this.uiRouter.stateService.target(MaintenanceModeStateNames.Default, transition.params('to'), {
        location: false,
        inherit: false,
      });
    });
    this.uiRouter.transitionService.onSuccess({}, transition => {
      if (this.pendingTransition === transition) this.pendingTransition = null;
      if (!this.isJourneyState(transition.to().name)) this.active = false;
    });
  }

  show(): void {
    if (this.active || !this.isJourneyRequest) return;
    this.active = true;
    const params = (this.pendingTransition ?? this.uiRouter.globals.transition)?.params('to') ?? this.uiRouter.globals.params;
    // Do not change the browser URL. Refresh retries the original server calls.
    void this.uiRouter.stateService.go(MaintenanceModeStateNames.Default, params, { location: false, inherit: false })
      .catch(() => {
        if (this.uiRouter.globals.current.name !== MaintenanceModeStateNames.Default &&
            (this.pendingTransition ?? this.uiRouter.globals.transition)?.to().name !== MaintenanceModeStateNames.Default) {
          this.active = false;
        }
      });
  }

  private isJourneyState(name: string | undefined): boolean {
    return name === MaintenanceModeStateNames.Default || name === 'client.journey' || !!name?.startsWith('client.journey.');
  }
}
